/**
 * Progression Drill — free-practice mode.
 *
 * Hear a chord progression; identify its Roman numeral pattern.
 */

import {
  useReducer,
  useRef,
  useEffect,
  useCallback,
  useState,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CoachSessionDoneCard } from "../components/CoachSessionDone.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import { ModeSwitcherBar, MODE_COLOR as PICKER_MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { ExerciseLessonLink } from "../components/ExerciseLessonLink.js";

import { useListenSession } from "../lib/use-listen-session.js";
import { useExerciseSession } from "../lib/use-exercise-session.js";
import { nextRoundSeed } from "../lib/round-seed.js";
import { ListenAutoAdvanceControl } from "../components/ListenAutoAdvanceControl.js";
import {
  buildProgressionExerciseRound,
  CADENCE_TEMPO_OPTS,
  DEFAULT_PROGRESSION_EXERCISE_CONFIG,
  progressionDrillItemId,
  progressionDrillStatsKey,
  PROGRESSION_POOL,
  PROGRESSION_ORDER,
  PROGRESSION_GROUPS,
  PROGRESSION_PRESETS,
  type ProgressionExerciseConfig,
  type ProgressionExerciseRound,
  type ProgressionKey,
  type ProgressionTempo,
} from "@music-app/exercise-generator";
import { createAudioPlayer, type AudioPlayer } from "@music-app/audio-engine-web";
import {
  useCardsStore,
  usePrefsStore,
  getDrillStats,
  upsertDrillStats,
  recordReview as dbRecordReview,
  type DrillStatsRecord,
} from "@music-app/persistence";
import { exerciseReviewSource, type Rating } from "@music-app/learning-system";
import { createUISoundPlayer, type UISoundPlayer } from "../lib/ui-sounds.js";
import { ChallengeTimer } from "../components/ChallengeTimer.js";
import { ChallengeScoreCard } from "../components/ChallengeScoreCard.js";
import { useChallengeTimer } from "../lib/use-challenge-timer.js";
import { getChallengeData, recordChallengeAttempt } from "@music-app/persistence";

// ─── Constants ────────────────────────────────────────────────────────────────

const SESSION_LENGTH_PRESETS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;

const TONIC_OPTIONS: { label: string; midi: number }[] = [
  { label: "C",  midi: 60 }, { label: "G",  midi: 67 },
  { label: "D",  midi: 62 }, { label: "A",  midi: 69 },
  { label: "E",  midi: 64 }, { label: "F",  midi: 65 },
  { label: "B♭", midi: 70 }, { label: "E♭", midi: 63 },
  { label: "A♭", midi: 68 }, { label: "D♭", midi: 61 },
];

// ─── State machine ────────────────────────────────────────────────────────────

type ProgressionExercisePhase = "config" | "playing" | "awaiting" | "reveal" | "graded" | "done";

interface SessionStat {
  correct: number;
  played: number;
}

interface ProgressionExerciseState {
  phase: ProgressionExercisePhase;
  config: ProgressionExerciseConfig;
  sessionLength: number;
  round: ProgressionExerciseRound | null;
  roundNumber: number;
  correctCount: number;
  selectedKey: ProgressionKey | null;
  wasCorrect: boolean | null;
  wasTimeout: boolean;
  sessionStats: Partial<Record<ProgressionKey, SessionStat>>;
}

type ProgressionExerciseAction =
  | { type: "START"; config: ProgressionExerciseConfig; sessionLength: number; round: ProgressionExerciseRound }
  | { type: "PLAYBACK_DONE" }
  | { type: "SELECT"; key: ProgressionKey }
  | { type: "TIMEOUT" }
  | { type: "NEXT_ROUND"; round: ProgressionExerciseRound }
  | { type: "SESSION_DONE" }
  | { type: "STOP" };

const INITIAL_STATE: ProgressionExerciseState = {
  phase: "config",
  config: DEFAULT_PROGRESSION_EXERCISE_CONFIG,
  sessionLength: DEFAULT_SESSION_LENGTH,
  round: null,
  roundNumber: 0,
  correctCount: 0,
  selectedKey: null,
  wasCorrect: null,
  wasTimeout: false,
  sessionStats: {},
};

function addStat(
  stats: Partial<Record<ProgressionKey, SessionStat>>,
  key: ProgressionKey,
  correct: boolean | null,
): Partial<Record<ProgressionKey, SessionStat>> {
  const prev = stats[key] ?? { correct: 0, played: 0 };
  return {
    ...stats,
    [key]: {
      played: prev.played + 1,
      correct: prev.correct + (correct === true ? 1 : 0),
    },
  };
}

function progressionDrillReducer(
  state: ProgressionExerciseState,
  action: ProgressionExerciseAction,
): ProgressionExerciseState {
  switch (action.type) {
    case "START":
      return {
        ...INITIAL_STATE,
        phase: "playing",
        config: action.config,
        sessionLength: action.sessionLength,
        round: action.round,
        roundNumber: 1,
      };

    case "PLAYBACK_DONE": {
      if (state.phase !== "playing") return state;
      if (state.config.drillMode === "listening" && state.round) {
        return {
          ...state,
          phase: "reveal",
          sessionStats: addStat(state.sessionStats, state.round.progressionKey, null),
        };
      }
      return { ...state, phase: "awaiting" };
    }

    case "SELECT": {
      if (state.phase !== "awaiting" || !state.round) return state;
      const correct = action.key === state.round.progressionKey;
      return {
        ...state,
        phase: "graded",
        selectedKey: action.key,
        wasCorrect: correct,
        wasTimeout: false,
        correctCount: state.correctCount + (correct ? 1 : 0),
        sessionStats: addStat(state.sessionStats, state.round.progressionKey, correct),
      };
    }

    case "TIMEOUT": {
      if (state.phase !== "awaiting" || !state.round) return state;
      return {
        ...state,
        phase: "graded",
        wasCorrect: false,
        wasTimeout: true,
        sessionStats: addStat(state.sessionStats, state.round.progressionKey, false),
      };
    }

    case "NEXT_ROUND":
      return {
        ...state,
        phase: "playing",
        round: action.round,
        roundNumber: state.roundNumber + 1,
        selectedKey: null,
        wasCorrect: null,
        wasTimeout: false,
      };

    case "SESSION_DONE":
      return { ...state, phase: "done" };

    case "STOP":
      return { ...INITIAL_STATE };
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ProgressionExercisePage(): ReactNode {
  const [searchParams] = useSearchParams();
  const VALID_MODES: ExerciseMode[] = ["listen", "practice", "review", "challenge"];
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: ExerciseMode = rawMode && VALID_MODES.includes(rawMode) ? rawMode : "practice";
  const listenMode = mode === "listen";
  const challengeMode = mode === "challenge";
  const coached = searchParams.get("coached") === "true";
  const coachItemCount = Math.max(1, parseInt(searchParams.get("itemCount") ?? "10", 10));
  const practiceMode = mode === "practice";
  const nodeId = searchParams.get("nodeId") ?? undefined;
  const from = searchParams.get("from") ?? undefined;

  const [state, dispatch] = useReducer(progressionDrillReducer, INITIAL_STATE);
  const playerRef = useRef<AudioPlayer | null>(null);
  const uiSoundRef = useRef<UISoundPlayer | null>(null);

  const challengeStartedAtRef = useRef<string | null>(null);
  const challengeCorrectRef = useRef(0);
  const challengeResponseTimesRef = useRef<number[]>([]);
  const responseStartRef = useRef<number | null>(null);
  const previousBestRef = useRef<number | null | undefined>(undefined);

  const { recordPlay } = useListenSession("progressions", listenMode, nodeId);
  const { recordItem } = useExerciseSession("progressions", mode === "review" ? "practice" : "drill", !listenMode && !challengeMode);

  const applyReview = useCardsStore((s) => s.applyReview);
  const instrument = usePrefsStore((s) => s.instrument);
  const uiSoundsEnabled = usePrefsStore((s) => s.uiSoundsEnabled);
  const cadenceTempo = usePrefsStore((s) => s.cadenceTempo);

  useEffect(() => {
    const player = createAudioPlayer();
    playerRef.current = player;
    return () => {
      player.stop();
      player.destroy();
      uiSoundRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !instrument) return;
    void (async () => {
      await player.init();
      if (!player.isInstrumentLoaded(instrument)) {
        await player.loadInstrument(instrument);
      }
      player.setInstrument(instrument);
    })();
  }, [instrument]);

  // Challenge mode: load previous best on mount.
  useEffect(() => {
    if (!challengeMode) return;
    challengeStartedAtRef.current = new Date().toISOString();
    challengeCorrectRef.current = 0;
    challengeResponseTimesRef.current = [];
    void getChallengeData("progressions").then((d) => {
      previousBestRef.current = d.best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeMode]);

  // Record response start time when awaiting answer (for all graded modes).
  useEffect(() => {
    if (state.phase !== "awaiting") return;
    responseStartRef.current = Date.now();
  }, [state.phase]);

  useEffect(() => {
    if (state.phase !== "graded" || !state.round) return;
    if (state.config.drillMode === "listening") return;
    const itemId = progressionDrillItemId(state.round.progressionKey);
    const correct = state.wasCorrect === true;
    const responseMs = responseStartRef.current
      ? Date.now() - responseStartRef.current
      : 8000;
    const chosenAnswerId =
      !correct && state.selectedKey !== null ? state.selectedKey : null;
    const source = exerciseReviewSource({ challengeMode, fsrsEnabled: state.config.fsrsEnabled });
    void dbRecordReview({
      itemId,
      sessionId: "",
      rating: correct ? "good" : "again",
      correct,
      responseMs,
      chosenAnswerId,
      source,
    });
    if (challengeMode) {
      challengeResponseTimesRef.current.push(responseMs);
      if (correct) {
        challengeCorrectRef.current += 1;
        const rating: Rating = responseMs < 5000 ? "good" : "hard";
        void applyReview(itemId, rating);
      } else {
        void applyReview(itemId, "again");
      }
      return;
    }
    if (!state.config.fsrsEnabled) return;
    void applyReview(itemId, correct ? "good" : "again");
  }, [state.phase, state.round, state.wasCorrect, state.selectedKey, state.config.fsrsEnabled, state.config.drillMode, challengeMode, applyReview]);

  useEffect(() => {
    if (state.phase !== "graded" || !state.round) return;
    void upsertDrillStats(
      progressionDrillStatsKey(state.round.progressionKey, state.config),
      "graded",
      state.wasCorrect ?? false,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  useEffect(() => {
    if (state.phase !== "reveal" || !state.round) return;
    void upsertDrillStats(
      progressionDrillStatsKey(state.round.progressionKey, state.config),
      "listening",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  useEffect(() => {
    if (state.phase !== "reveal" || !listenMode || !state.round) return;
    recordPlay(progressionDrillItemId(state.round.progressionKey));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  // Challenge mode: record attempt on session done.
  useEffect(() => {
    if (state.phase !== "done" || !challengeMode) return;
    const times = challengeResponseTimesRef.current;
    const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
    const correct = challengeCorrectRef.current;
    void recordChallengeAttempt({
      challengeKey: "progressions",
      startedAt: challengeStartedAtRef.current ?? new Date().toISOString(),
      items: state.sessionLength,
      correct,
      accuracy: state.sessionLength > 0 ? correct / state.sessionLength : 0,
      averageResponseMs: Math.round(avgMs),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // Challenge mode: auto-advance 600ms after timeout.
  useEffect(() => {
    if (!state.wasTimeout) return;
    const id = window.setTimeout(() => nextRound(), 600);
    return () => window.clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.wasTimeout]);

  const playRound = useCallback(
    async (round: ProgressionExerciseRound, mode: ProgressionExerciseConfig["drillMode"]) => {
      const player = playerRef.current;
      if (!player) return;
      player.stop();
      await player.init();
      const pref = usePrefsStore.getState().instrument;
      if (!player.isInstrumentLoaded(pref)) await player.loadInstrument(pref);
      player.setInstrument(pref);
      await player.play(round.audioPlan);
      if (mode === "listening") {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 2000));
        await player.play(round.teachAudio);
      }
      recordItem(progressionDrillItemId(round.progressionKey));
      dispatch({ type: "PLAYBACK_DONE" });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recordItem],
  );

  const replay = useCallback(() => {
    const player = playerRef.current;
    if (!player || !state.round) return;
    void player.play(state.round.audioPlan);
  }, [state.round]);

  const startDrill = useCallback(
    (config: ProgressionExerciseConfig, sessionLength: number) => {
      const round = buildProgressionExerciseRound(config, nextRoundSeed(), CADENCE_TEMPO_OPTS[cadenceTempo]);
      dispatch({ type: "START", config, sessionLength, round });
      void playRound(round, config.drillMode);
    },
    [playRound, cadenceTempo],
  );

  const handleSelect = useCallback(
    (key: ProgressionKey) => {
      if (!state.round || state.phase !== "awaiting") return;
      const correct = key === state.round.progressionKey;
      dispatch({ type: "SELECT", key });
      if (uiSoundsEnabled) {
        if (!uiSoundRef.current) uiSoundRef.current = createUISoundPlayer();
        uiSoundRef.current.play(correct ? "correct" : "wrong");
      }
      if (!correct && state.round.teachAudio) {
        const teachPlan = state.round.teachAudio;
        const player = playerRef.current;
        if (player) window.setTimeout(() => { void player.play(teachPlan); }, 600);
      }
    },
    [state.round, state.phase, uiSoundsEnabled],
  );

  const nextRound = useCallback(() => {
    if (!state.round) return;
    if (state.roundNumber >= state.sessionLength) {
      dispatch({ type: "SESSION_DONE" });
      return;
    }
    const round = buildProgressionExerciseRound(state.config, nextRoundSeed(), CADENCE_TEMPO_OPTS[cadenceTempo]);
    dispatch({ type: "NEXT_ROUND", round });
    void playRound(round, state.config.drillMode);
  }, [state.config, state.round, state.roundNumber, state.sessionLength, playRound, cadenceTempo]);

  const handleStop = useCallback(() => {
    playerRef.current?.stop();
    dispatch({ type: "STOP" });
  }, []);

  const handleTimeout = useCallback(() => {
    dispatch({ type: "TIMEOUT" });
  }, []);

  const { remainingMs: challengeRemainingMs } = useChallengeTimer({
    active: challengeMode && state.phase === "awaiting",
    timeMs: 8000,
    onTimeout: handleTimeout,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key.toLowerCase() === "r" && state.phase !== "config" && state.phase !== "done") {
        e.preventDefault();
        replay();
        return;
      }
      if ((state.phase === "graded" || state.phase === "reveal") &&
          (e.key === " " || e.key === "Enter")) {
        e.preventDefault();
        nextRound();
        return;
      }
      if (state.phase === "awaiting") {
        const idx = parseInt(e.key, 10) - 1;
        const buttons = PROGRESSION_ORDER.filter((k) =>
          state.config.progressions.includes(k),
        );
        if (idx >= 0 && idx < buttons.length) {
          e.preventDefault();
          handleSelect(buttons[idx]!);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.phase, state.config.progressions, nextRound, replay, handleSelect]);

  const modeColor = PICKER_MODE_COLOR[mode];

  if (state.phase === "config") {
    if (challengeMode) return <ProgressionExerciseChallengeStart onStart={startDrill} />;
    if (coached) return <ProgressionExerciseCoachStart itemCount={coachItemCount} onStart={startDrill} />;
    return <ProgressionExerciseConfigScreen mode={mode} onStart={startDrill} />;
  }

  if (state.phase === "done") {
    if (coached) return <CoachSessionDoneCard />;
    if (challengeMode) {
      const times = challengeResponseTimesRef.current;
      const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
      return (
        <ChallengeScoreCard
          items={state.sessionLength}
          correct={challengeCorrectRef.current}
          averageResponseMs={avgMs}
          previousBest={previousBestRef.current ?? null}
          onAgain={() => startDrill(state.config, state.sessionLength)}
          onStop={handleStop}
        />
      );
    }
    return (
      <ProgressionExerciseSummary
        config={state.config}
        sessionLength={state.sessionLength}
        correctCount={state.correctCount}
        sessionStats={state.sessionStats}
        onAgain={() => startDrill(state.config, state.sessionLength)}
        onStop={handleStop}
      />
    );
  }

  return (
    <ProgressionExerciseLoop
      state={state}
      listenMode={listenMode}
      practiceMode={practiceMode}
      challengeMode={challengeMode}
      challengeRemainingMs={challengeRemainingMs}
      modeColor={modeColor}
      onSelect={handleSelect}
      onNext={nextRound}
      onReplay={replay}
      onStop={handleStop}
      from={from}
    />
  );
}

// ─── Coached listen auto-start ───────────────────────────────────────────────

function ProgressionExerciseCoachStart({
  itemCount,
  onStart,
}: {
  itemCount: number;
  onStart: (config: ProgressionExerciseConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    onStart({ ...DEFAULT_PROGRESSION_EXERCISE_CONFIG, drillMode: "listening", fsrsEnabled: false }, itemCount);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting listen session…</p>
    </div>
  );
}

// ─── Challenge mode auto-start ────────────────────────────────────────────────

function ProgressionExerciseChallengeStart({
  onStart,
}: {
  onStart: (config: ProgressionExerciseConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    onStart({ ...DEFAULT_PROGRESSION_EXERCISE_CONFIG, drillMode: "graded", fsrsEnabled: false }, 20);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting challenge…</p>
    </div>
  );
}

// ─── Listen mode auto-start ───────────────────────────────────────────────────

// ─── Config persistence ───────────────────────────────────────────────────────

const PROGRESSION_DRILL_LS_KEY = "progression-drill-config-v2";

interface ProgressionExerciseSavedConfig {
  sessionLength: number;
  mode: ExerciseMode;
  tempo: ProgressionTempo;
  tonic: ProgressionExerciseConfig["tonic"];
  fixedTonicMidi: number;
  selectedProgressions: ProgressionKey[];
}

const PROGRESSION_DRILL_DEFAULTS: ProgressionExerciseSavedConfig = {
  sessionLength: DEFAULT_SESSION_LENGTH,
  mode: "practice",
  tempo: "medium",
  tonic: "random",
  fixedTonicMidi: 60,
  selectedProgressions: [...DEFAULT_PROGRESSION_EXERCISE_CONFIG.progressions],
};

function loadProgressionExerciseConfig(): ProgressionExerciseSavedConfig {
  try {
    const raw = localStorage.getItem(PROGRESSION_DRILL_LS_KEY);
    if (raw) {
      return {
        ...PROGRESSION_DRILL_DEFAULTS,
        ...(JSON.parse(raw) as Partial<ProgressionExerciseSavedConfig>),
      };
    }
  } catch { /* ignore */ }
  return PROGRESSION_DRILL_DEFAULTS;
}

function saveProgressionExerciseConfig(cfg: ProgressionExerciseSavedConfig): void {
  try { localStorage.setItem(PROGRESSION_DRILL_LS_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
}

// ─── Config screen ────────────────────────────────────────────────────────────

// Preselect the progression set for the lesson's node when arriving via
// "Practice this" (`?nodeId=…`). Only the nodes with a clean preset are mapped;
// slash-chords and modulation have no matching progression preset, so they keep
// the persisted default.
function progressionNodeConfigPatch(
  nodeId: string | undefined,
): Partial<ProgressionExerciseSavedConfig> {
  if (nodeId === "diatonic-progressions")
    return { selectedProgressions: [...(PROGRESSION_PRESETS.diatonic ?? [])] };
  if (nodeId === "secondary-dominant-recognition")
    return { selectedProgressions: [...(PROGRESSION_PRESETS.all ?? [])] };
  return {};
}

interface ProgressionExerciseConfigScreenProps {
  mode: ExerciseMode;
  onStart: (config: ProgressionExerciseConfig, sessionLength: number) => void;
}

function ProgressionExerciseConfigScreen({ mode, onStart }: ProgressionExerciseConfigScreenProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const nodeId = searchParams.get("nodeId") ?? undefined;
  const [initConfig] = useState(() => ({ ...loadProgressionExerciseConfig(), ...progressionNodeConfigPatch(nodeId) }));

  const [sessionLength, setSessionLength] = useState(initConfig.sessionLength);
  const [showCustomLength, setShowCustomLength] = useState(
    !SESSION_LENGTH_PRESETS.includes(initConfig.sessionLength),
  );
  const modeColor = PICKER_MODE_COLOR[mode];
  const [tempo, setTempo] = useState<ProgressionTempo>(initConfig.tempo);
  const [tonic, setTonic] = useState<ProgressionExerciseConfig["tonic"]>(initConfig.tonic);
  const [fixedTonicMidi, setFixedTonicMidi] = useState(initConfig.fixedTonicMidi);
  const [selectedProgressions, setSelectedProgressions] = useState<Set<ProgressionKey>>(
    () => new Set(initConfig.selectedProgressions),
  );
  const [historyStats, setHistoryStats] = useState<
    Partial<Record<string, DrillStatsRecord | null>>
  >({});

  useEffect(() => {
    const cfgSnap = { tempo, tonic, fixedTonicMidi };
    void Promise.all(
      Array.from(selectedProgressions).map((k) => {
        const key = progressionDrillStatsKey(k, cfgSnap);
        return getDrillStats(key).then((s) => [key, s ?? null] as const);
      }),
    ).then((entries) => setHistoryStats(Object.fromEntries(entries)));
  }, [selectedProgressions, tempo, tonic, fixedTonicMidi]);

  useEffect(() => {
    saveProgressionExerciseConfig({
      sessionLength, mode, tempo, tonic, fixedTonicMidi,
      selectedProgressions: Array.from(selectedProgressions),
    });
  }, [sessionLength, mode, tempo, tonic, fixedTonicMidi, selectedProgressions]);

  const toggleProgression = (key: ProgressionKey) => {
    setSelectedProgressions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size <= 2) return prev;
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleStart = () => {
    if (selectedProgressions.size < 2) return;
    onStart(
      {
        progressions: PROGRESSION_ORDER.filter((k) => selectedProgressions.has(k)),
        tempo,
        tonic,
        fixedTonicMidi,
        drillMode: mode === "listen" ? "listening" : "graded",
        fsrsEnabled: mode === "practice",
      },
      sessionLength,
    );
  };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        title="Progression Recognition"
        subhead="Hear a chord progression — name its Roman numeral pattern."
      />

      <ModeSwitcherBar current={mode} exercisePath="/exercise/progressions" />
      <ExerciseLessonLink nodeIds={["diatonic-progressions", "secondary-dominant-recognition", "modulation-recognition"]} />

      <div className="space-y-5 mb-10">
        <ConfigRow label="Exercises per session">
          <div className="flex items-center gap-3 flex-wrap">
            {SESSION_LENGTH_PRESETS.map((n) => {
              const active = !showCustomLength && sessionLength === n;
              return (
                <button
                  key={n}
                  type="button"
                  aria-pressed={active}
                  onClick={() => { setSessionLength(n); setShowCustomLength(false); }}
                  className={`px-4 py-2 text-sm border transition-colors ${
                    active ? "" : "bg-paper text-ink border-ink/20 hover:border-ink"
                  }`}
                  style={active ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
                >
                  {n}
                </button>
              );
            })}
            <button
              type="button"
              aria-pressed={showCustomLength}
              onClick={() => setShowCustomLength(true)}
              className={`px-4 py-2 text-sm border transition-colors ${
                showCustomLength ? "" : "bg-paper text-ink border-ink/20 hover:border-ink"
              }`}
              style={showCustomLength ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
            >
              Other…
            </button>
            {showCustomLength && (
              <input
                type="number"
                min={1}
                max={50}
                value={sessionLength}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 50) setSessionLength(v);
                }}
                className="w-16 px-3 py-2 text-sm border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
                aria-label="Custom number of exercises"
                autoFocus
              />
            )}
          </div>
        </ConfigRow>

        <ConfigRow label="Tempo">
          <NativeSelect
            value={tempo}
            onChange={(v) => setTempo(v as ProgressionTempo)}
            options={[
              { value: "slow",   label: "Slow  (1.4 s / chord)" },
              { value: "medium", label: "Medium  (0.95 s / chord)" },
              { value: "fast",   label: "Fast  (0.65 s / chord)" },
            ]}
          />
        </ConfigRow>

        <ConfigRow label="Tonic">
          <div className="flex gap-3 flex-wrap items-center">
            <NativeSelect
              value={tonic}
              onChange={(v) => setTonic(v as ProgressionExerciseConfig["tonic"])}
              options={[
                { value: "random", label: "Random" },
                { value: "fixed",  label: "Fixed" },
              ]}
            />
            {tonic === "fixed" && (
              <NativeSelect
                value={String(fixedTonicMidi)}
                onChange={(v) => setFixedTonicMidi(Number(v))}
                options={TONIC_OPTIONS.map((t) => ({ value: String(t.midi), label: t.label }))}
              />
            )}
          </div>
        </ConfigRow>
      </div>

      {/* Progression pool */}
      <div className="mb-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Progression pool <span className="text-inkFaded/60">(minimum 2)</span>
        </p>

        <div className="flex gap-2 mb-4 flex-wrap">
          {(["common", "diatonic", "all"] as const).map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() =>
                setSelectedProgressions(new Set(PROGRESSION_PRESETS[preset]))
              }
              className="px-3 py-1.5 text-xs font-mono border border-ink/20 text-ink hover:border-ink transition-colors"
            >
              {preset === "common" ? "Common 4" : preset === "diatonic" ? "Diatonic" : "All"}
            </button>
          ))}
        </div>

        <div className="border border-ink/10">
          {PROGRESSION_GROUPS.map((group) => (
            <div key={group.label} className="border-b border-ink/10 last:border-b-0">
              <div className="px-4 py-1.5 bg-paper border-b border-ink/5">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">
                  {group.label}
                </span>
              </div>
              {group.progressions.map((key) => {
                const info = PROGRESSION_POOL[key];
                const checked = selectedProgressions.has(key);
                const wouldViolateMin = checked && selectedProgressions.size <= 2;
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-ink/10 last:border-b-0 ${
                      checked ? "text-ink" : "text-inkFaded"
                    } ${wouldViolateMin ? "cursor-not-allowed" : "hover:bg-ink/5"}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleProgression(key)}
                      disabled={wouldViolateMin}
                      className="accent-ink w-3.5 h-3.5 shrink-0"
                      aria-label={`Include ${info.displayLabel}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-sm font-medium">{info.displayLabel}</div>
                      <div className="font-mono text-[10px] text-inkFaded/70">{info.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* History table */}
      <ProgressionHistoryTable
        progressions={PROGRESSION_ORDER.filter((k) => selectedProgressions.has(k))}
        tonicConfig={{ tempo, tonic, fixedTonicMidi }}
        historyStats={historyStats}
      />

      <button
        type="button"
        onClick={handleStart}
        disabled={selectedProgressions.size < 2}
        className={`mt-8 ${buttonClasses("primary")}`}
        style={{ backgroundColor: modeColor }}
      >
        Start →
      </button>
    </div>
  );
}

// ─── Drill loop ───────────────────────────────────────────────────────────────

interface ProgressionExerciseLoopProps {
  state: ProgressionExerciseState;
  listenMode: boolean;
  practiceMode: boolean;
  challengeMode: boolean;
  challengeRemainingMs: number;
  modeColor: string;
  onSelect: (key: ProgressionKey) => void;
  onNext: () => void;
  onReplay: () => void;
  onStop: () => void;
  from?: string;
}

function ProgressionExerciseLoop({
  state,
  listenMode,
  practiceMode,
  challengeMode,
  challengeRemainingMs,
  modeColor,
  onSelect,
  onNext,
  onReplay,
  onStop,
  from,
}: ProgressionExerciseLoopProps): ReactNode {
  const { phase, round, roundNumber, sessionLength, config, selectedKey, wasCorrect, wasTimeout } = state;

  const isPlaying  = phase === "playing";
  const isAwaiting = phase === "awaiting";
  const isReveal   = phase === "reveal";
  const isGraded   = phase === "graded";

  const isLastRound = roundNumber >= sessionLength;
  const answeredCount = isGraded || isReveal ? roundNumber : roundNumber - 1;

  const progressionButtons = PROGRESSION_ORDER.filter((k) =>
    config.progressions.includes(k),
  );
  const useGrid3 = progressionButtons.length >= 5;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8">
      <div className="flex items-center gap-3 mb-4">
        {from === "today" && (
          <>
            <Link
              to="/"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Today
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {from === "learn" && (
          <>
            <Link
              to="/learn"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Learn
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {!from && (
          <>
            <Link
              to="/exercise"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Ear Training
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        <button
          type="button"
          onClick={onStop}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
          aria-label="Return to drill settings"
        >
          ← Settings
        </button>
      </div>
      <ModeSwitcherBar current={challengeMode ? "challenge" : practiceMode ? "review" : listenMode ? "listen" : "practice"} />

      {challengeMode && isAwaiting && (
        <ChallengeTimer totalMs={8000} remainingMs={challengeRemainingMs} />
      )}

      <div className="flex items-baseline justify-between mb-1">
        <h1 className="font-serif text-4xl font-medium text-ink">Progression Drill</h1>
        <p className="font-mono text-xs text-inkFaded tabular-nums" aria-label={`Exercise ${roundNumber} of ${sessionLength}`}>
          {roundNumber} of {sessionLength}
        </p>
      </div>
      <p className="font-mono text-xs text-inkFaded mb-4">
        {config.tempo} tempo · {config.tonic === "random" ? "random tonic" : "fixed tonic"}
      </p>

      <div
        className="w-full h-0.5 bg-ink/10 mb-2"
        role="progressbar"
        aria-valuenow={roundNumber}
        aria-valuemin={1}
        aria-valuemax={sessionLength}
      >
        <div
          className="h-full transition-all duration-300"
          style={{ width: `${(roundNumber / sessionLength) * 100}%`, backgroundColor: modeColor }}
        />
      </div>

      <div className="flex justify-between font-mono text-xs text-inkFaded mb-6">
        {config.drillMode !== "listening" ? (
          <span>{state.correctCount} correct of {answeredCount} answered</span>
        ) : (
          <span>{answeredCount} of {sessionLength} listened</span>
        )}
        {isPlaying && <span className="animate-pulse">Playing…</span>}
      </div>

      {round && (
        <div className="mb-4 font-mono text-xs text-inkFaded">
          Key: <span className="text-ink">{round.keyName}</span>
        </div>
      )}

      {/* Roman numeral chips (reveal + graded) */}
      {(isReveal || isGraded) && round && (
        <div className="mb-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded mb-1.5">
            {isReveal ? "That was" : wasCorrect ? "Correct —" : wasTimeout ? "Time! —" : "The answer was"}
          </p>
          <div className="flex flex-wrap gap-1.5" role="list" aria-label="Chord progression">
            {PROGRESSION_POOL[round.progressionKey].degrees.map((deg, i) => {
              const romanLabel = degreeToRomanLabel(
                deg,
                PROGRESSION_POOL[round.progressionKey].perIndexQualityOverrides?.[i],
              );
              const isLast = i === PROGRESSION_POOL[round.progressionKey].degrees.length - 1;
              return (
                <span
                  key={i}
                  role="listitem"
                  className="inline-block px-3 py-1.5 font-serif text-sm border"
                  style={isLast
                    ? { borderColor: modeColor, color: modeColor, backgroundColor: `${modeColor}18` }
                    : { borderColor: "rgba(26,24,19,0.2)", color: "var(--color-ink)" }}
                >
                  {romanLabel}
                </span>
              );
            })}
          </div>
          <p className="mt-2 font-mono text-[10px] text-inkFaded italic">
            {PROGRESSION_POOL[round.progressionKey].description}
          </p>
          {isGraded && !challengeMode && (
            <p className="font-mono text-[10px] mt-1" style={{ color: practiceMode ? modeColor : "#9A9390" }}>
              {practiceMode ? "Counts toward Today." : "No schedule changes."}
            </p>
          )}
        </div>
      )}

      {/* Prompt */}
      <div className="mb-6 min-h-[3rem] flex items-center">
        {isPlaying && (
          <p className="font-serif italic text-xl text-inkSoft" aria-live="polite">Listen…</p>
        )}
        {isAwaiting && (
          <p className="font-serif italic text-xl text-ink" aria-live="polite">
            Which progression did you hear?
          </p>
        )}
        {isGraded && !wasCorrect && round && (
          <p className="font-serif italic text-ink" aria-live="polite">
            You selected{" "}
            <span className="line-through text-inkFaded">
              {PROGRESSION_POOL[selectedKey!]?.displayLabel}
            </span>
            .
          </p>
        )}
      </div>

      {/* Answer buttons */}
      <div className="mb-6">
        <div className={`grid gap-2 mb-2 ${useGrid3 ? "grid-cols-3" : "grid-cols-2"}`}>
          {progressionButtons.map((key) => {
            const info = PROGRESSION_POOL[key];
            const isCorrect  = key === round?.progressionKey;
            const isSelected = key === selectedKey;
            const showCorrect = (isGraded || isReveal) && isCorrect;
            const showWrong   = isGraded && isSelected && !isCorrect;
            const disabled    = isPlaying || isReveal || isGraded;

            return (
              <button
                key={key}
                type="button"
                disabled={disabled}
                onClick={() => onSelect(key)}
                aria-pressed={isSelected && isAwaiting}
                aria-label={`${info.displayLabel} — ${info.description}`}
                className={`px-3 py-3 border text-left transition-colors ${
                  showCorrect
                    ? "bg-success/10 border-success text-success"
                    : showWrong
                    ? "bg-danger/10 border-danger text-danger"
                    : disabled
                    ? "bg-paper border-ink/10 text-inkFaded"
                    : "bg-paper border-ink/20 text-ink hover:border-ink hover:bg-ink/5"
                }`}
              >
                <div className="font-serif text-sm font-medium leading-tight">
                  {info.displayLabel}
                </div>
                <div className="font-mono text-[10px] text-inkFaded mt-0.5">{info.description}</div>
              </button>
            );
          })}
        </div>
        {isAwaiting && (
          <p className="mt-2 font-mono text-[10px] text-inkFaded">1–{progressionButtons.length} to answer · R to replay</p>
        )}
        {(isReveal || isGraded) && (
          <p className="mt-2 font-mono text-[10px] text-inkFaded">
            {isLastRound ? "Space/Enter to finish" : "Space/Enter for next"} · R
          </p>
        )}
      </div>

      <div className="flex gap-3 flex-wrap">
        {!challengeMode && (
          <button
            type="button"
            disabled={!isReveal && !isGraded}
            onClick={onNext}
            className={buttonClasses("primary")}
            style={{ backgroundColor: modeColor }}
          >
            {isLastRound ? "Finish →" : "Next →"}
          </button>
        )}
        {!challengeMode && (
          <button
            type="button"
            disabled={isPlaying}
            onClick={onReplay}
            className={buttonClasses("secondary")}
            aria-label="Replay the progression"
          >
            ♩ Replay
          </button>
        )}
      </div>
      {listenMode && <ListenAutoAdvanceControl isReveal={isReveal} onAdvance={onNext} />}
    </div>
  );
}

// ─── Session summary ──────────────────────────────────────────────────────────

interface ProgressionExerciseSummaryProps {
  config: ProgressionExerciseConfig;
  sessionLength: number;
  correctCount: number;
  sessionStats: Partial<Record<ProgressionKey, SessionStat>>;
  onAgain: () => void;
  onStop: () => void;
}

function ProgressionExerciseSummary({
  config,
  sessionLength,
  correctCount,
  sessionStats,
  onAgain,
  onStop,
}: ProgressionExerciseSummaryProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const isGraded = config.drillMode === "graded";
  const accuracy = isGraded ? Math.round((correctCount / sessionLength) * 100) : null;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        eyebrow="progression"
        title="Session complete"
      />

      <div className="grid grid-cols-2 border border-ink/10 mb-8">
        <div className="p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">Exercises</p>
          <p className="font-serif text-3xl font-medium text-ink">{sessionLength}</p>
        </div>
        {accuracy !== null && (
          <div className="p-6 border-l border-ink/10">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">Correct</p>
            <p className="font-serif text-3xl font-medium text-ink">
              {accuracy}<span className="text-lg text-inkSoft font-serif italic ml-0.5">%</span>
            </p>
          </div>
        )}
      </div>

      {Object.keys(sessionStats).length > 0 && (
        <div className="mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">By progression</p>
          <div className="border border-ink/10">
            <div className="grid grid-cols-[1fr_4rem_4rem_3.5rem] px-4 py-2 border-b border-ink/10">
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Progression</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Played</span>
              {isGraded && (
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Correct</span>
              )}
              {isGraded && (
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">%</span>
              )}
            </div>
            {PROGRESSION_ORDER.filter((k) => sessionStats[k] !== undefined).map((key) => {
              const stat = sessionStats[key]!;
              const pct =
                isGraded && stat.played > 0
                  ? Math.round((stat.correct / stat.played) * 100)
                  : null;
              const info = PROGRESSION_POOL[key];
              return (
                <div
                  key={key}
                  className="grid grid-cols-[1fr_4rem_4rem_3.5rem] px-4 py-2.5 border-b border-ink/10 last:border-b-0"
                >
                  <span className="font-serif text-sm text-ink">{info.displayLabel}</span>
                  <span className="font-mono text-xs text-ink text-right tabular-nums">{stat.played}</span>
                  {isGraded && (
                    <span className="font-mono text-xs text-ink text-right tabular-nums">{stat.correct}</span>
                  )}
                  {isGraded && (
                    <span className="font-mono text-xs text-inkFaded text-right tabular-nums">
                      {pct !== null ? `${pct}%` : "—"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-3 flex-wrap items-center">
        <button
          type="button"
          onClick={onAgain}
          className={buttonClasses("primary")}
        >
          Drill again →
        </button>
        <button
          type="button"
          onClick={onStop}
          className={buttonClasses("secondary")}
        >
          Change settings
        </button>
        <Link
          to="/exercise"
          className={buttonClasses("secondary")}
        >
          All drills
        </Link>
        <Link
          to="/stats"
          className={buttonClasses("secondary")}
        >
          See your stats →
        </Link>
        <ExerciseLessonLink nodeIds={["diatonic-progressions", "secondary-dominant-recognition", "modulation-recognition"]} />
      </div>
    </div>
  );
}

// ─── History table ────────────────────────────────────────────────────────────

interface ProgressionHistoryTableProps {
  progressions: readonly ProgressionKey[];
  tonicConfig: Pick<ProgressionExerciseConfig, "tempo" | "tonic" | "fixedTonicMidi">;
  historyStats: Partial<Record<string, DrillStatsRecord | null>>;
}

function ProgressionHistoryTable({
  progressions,
  tonicConfig,
  historyStats,
}: ProgressionHistoryTableProps): ReactNode {
  const rows = progressions
    .map((k) => ({
      key: k,
      stats: historyStats[progressionDrillStatsKey(k, tonicConfig)] ?? null,
    }))
    .filter((r): r is { key: ProgressionKey; stats: DrillStatsRecord } =>
      r.stats !== null && r.stats.played > 0,
    );

  const [open, setOpen] = useState(false);
  if (rows.length === 0) return null;
  return (
    <div className="mb-8">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors mb-3"
        aria-expanded={open}
      >
        History
        <span aria-hidden className="text-[8px]">{open ? "▴" : "▾"}</span>
      </button>
      <div className={open ? "border border-ink/10" : "hidden"}>
        <div className="grid grid-cols-[1fr_4rem_4rem_4rem_3rem] px-4 py-2 border-b border-ink/10">
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Progression</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Listen</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Practice</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Correct</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">%</span>
        </div>
        {rows.map(({ key, stats: s }) => {
          const listened = Math.max(0, s.played - s.correct - s.errors);
          const graded = s.correct + s.errors;
          const pct = graded > 0 ? Math.round((s.correct / graded) * 100) : null;
          const info = PROGRESSION_POOL[key];
          return (
            <div
              key={key}
              className="grid grid-cols-[1fr_4rem_4rem_4rem_3rem] px-4 py-2.5 border-b border-ink/10 last:border-b-0"
            >
              <span className="font-serif text-sm text-ink">{info.displayLabel}</span>
              <span className="font-mono text-xs text-ink text-right tabular-nums">{listened}</span>
              <span className="font-mono text-xs text-ink text-right tabular-nums">{graded}</span>
              <span className="font-mono text-xs text-ink text-right tabular-nums">{graded > 0 ? s.correct : "—"}</span>
              <span className="font-mono text-xs text-inkFaded text-right tabular-nums">{pct !== null ? `${pct}%` : "—"}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ROMAN_NUMERALS: Record<number, { upper: string; lower: string }> = {
  1: { upper: "I",   lower: "i"   },
  2: { upper: "II",  lower: "ii"  },
  3: { upper: "III", lower: "iii" },
  4: { upper: "IV",  lower: "iv"  },
  5: { upper: "V",   lower: "v"   },
  6: { upper: "VI",  lower: "vi"  },
  7: { upper: "VII", lower: "vii" },
};

function degreeToRomanLabel(degree: number, qualityOverride?: string): string {
  const rn = ROMAN_NUMERALS[degree];
  if (!rn) return String(degree);
  // Major triad or dom7 = uppercase; minor triad = lowercase; dim = lowercase+°
  if (qualityOverride) {
    if (qualityOverride === "dom7") return `${rn.upper}⁷`;
    if (qualityOverride === "maj")  return rn.upper;
  }
  // Diatonic major quality degrees in major: I, IV, V
  const majorDegrees = [1, 4, 5];
  const minorDegrees = [2, 3, 6];
  const dimDegrees   = [7];
  if (majorDegrees.includes(degree)) return rn.upper;
  if (minorDegrees.includes(degree)) return rn.lower;
  if (dimDegrees.includes(degree))   return `${rn.lower}°`;
  return rn.upper;
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function ConfigRow({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-2">{label}</p>
      {children}
    </div>
  );
}

function NativeSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}): ReactNode {
  return (
    <div className="relative inline-block">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-3 pr-8 py-2 bg-paper border border-ink/20 font-mono text-sm text-ink focus:outline-none focus:border-ink appearance-none cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-inkFaded text-[10px]">
        ▾
      </span>
    </div>
  );
}
